import { Component } from '@theme/component';

/**
 * @typedef {Object} ShareRefs
 * @property {HTMLElement} shareButton - The button to toggle the share dialog
 */

/**
 * A custom element that provides a share button using the Web Share API
 * with graceful fallbacks when that API is unavailable.
 *
 * @extends {Component<ShareRefs>}
 */
class ShareButtonComponent extends Component {
  requiredRefs = ['shareButton'];

  constructor() {
    super();
    this.shareSupported = false;
    this.clipboardSupported = false;
    /** @type {HTMLElement | null} */
    this.tooltipEl = null;
    this.tooltipTimeoutId = null;
  }

  connectedCallback() {
    super.connectedCallback();

    this.shareSupported = typeof navigator.share === 'function';
    this.clipboardSupported = Boolean(navigator.clipboard?.writeText);

    if (!window.isSecureContext) {
      this.style.display = 'none';
      return;
    }

    if (!this.shareSupported && !this.clipboardSupported) {
      this.style.display = 'none';
      return;
    }

    if (this.refs.shareButton) {
      if (!this.shareSupported && this.clipboardSupported) {
        const copyLabel = this.refs.shareButton.dataset.copyAriaLabel || 'Copy link to clipboard';

        this.refs.shareButton.setAttribute('aria-label', copyLabel);
      }

      const tooltipHost = this.querySelector('tooltip-element[data-share-tooltip]');
      this.tooltipEl = tooltipHost instanceof HTMLElement ? tooltipHost : null;
      this.refs.shareButton.addEventListener('click', this.onClick);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.refs.shareButton) {
      this.refs.shareButton.removeEventListener('click', this.onClick);
    }

    if (this.tooltipTimeoutId) {
      window.clearTimeout(this.tooltipTimeoutId);
      this.tooltipTimeoutId = null;
    }
  }

  /**
   * @param {MouseEvent} e
   */
  onClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    const shareData = {
      title: document.title,
      url: window.location.href,
    };

    if (this.shareSupported) {
      try {
        await navigator.share(shareData);
        return;
      } catch (err) {
        if (err?.name === 'AbortError') {
          return;
        }
        console.warn('Share failed, falling back to copy:', err);
      }
    }

    this.tryClipboardFallback(shareData.url);
  };

  /**
   * @param {string} url
   */
  tryClipboardFallback(url) {
    if (this.clipboardSupported) {
      navigator.clipboard
        .writeText(url)
        .then(() => {
          this.handleCopied(url);
        })
        .catch((err) => {
          console.warn('Copy to clipboard failed, falling back to prompt:', err);
          this.showPromptFallback(url);
        });
      return;
    }

    this.showPromptFallback(url);
  }

  /**
   * @param {string} url
   */
  showPromptFallback(url) {
    const label = this.refs.shareButton?.dataset.promptLabel || 'Copy this link';

    window.prompt(label, url);
  }

  /**
   * @param {string} url
   */
  handleCopied(url) {
    this.dispatchEvent(
      new CustomEvent('share:copied', {
        bubbles: true,
        detail: { url },
      })
    );

    this.showCopiedTooltip();
  }

  showCopiedTooltip() {
    const button = this.refs.shareButton;
    if (!button) return;

    const tooltipEl = this.tooltipEl;
    if (!(tooltipEl instanceof HTMLElement)) {
      console.warn('[share-button]: missing tooltip-element; ensure markup includes it.');
      return;
    }

    const label = button.dataset.copiedLabel || 'Copied';
    tooltipEl.setAttribute('text', label);
    tooltipEl.setAttribute('aria-live', 'polite');

    if (typeof tooltipEl.show === 'function') {
      tooltipEl.show();
    }

    if (this.tooltipTimeoutId) {
      window.clearTimeout(this.tooltipTimeoutId);
    }

    this.tooltipTimeoutId = window.setTimeout(() => {
      if (typeof tooltipEl.hide === 'function') {
        tooltipEl.hide();
      }

      this.tooltipTimeoutId = null;
    }, 3000);
  }
}

if (!customElements.get('share-button')) {
  customElements.define('share-button', ShareButtonComponent);
}
